/**
 * The merge.
 *
 * This module is pure and has no I/O, which is deliberate: it holds the only
 * logic in the sync layer that can silently lose someone's work, so it has to
 * be testable exhaustively without a network or a database.
 *
 * The rule is last-write-wins PER ITEM, never per week. Whole-plan LWW would
 * mean that ticking things off in a shop with no signal, while the other phone
 * adds "wine" from home, ends with one of those two edits destroyed on the next
 * sync. Per item, both survive.
 */

export interface ItemMeta {
  /** ISO-8601, UTC. */
  updatedAt: string;
  /** Device id — see `deviceId()`. Also the tie-break. */
  updatedBy: string;
  /** A tombstone. The item is gone, but its absence has a timestamp. */
  deleted?: true;
}

export interface SyncItem<T = unknown> {
  key: string;
  /** `null` exactly when `meta.deleted` is set. */
  value: T | null;
  meta: ItemMeta;
}

/**
 * A deleted item keeps a dated tombstone rather than just vanishing. Without
 * one, unplanning Tuesday's dinner would be silently undone on the next sync
 * by the other phone's older copy of that slot — the delete has no timestamp
 * to argue with, so the stale edit always looks newer than nothing.
 */
export function tombstone(key: string, meta: Omit<ItemMeta, 'deleted'>): SyncItem<never> {
  return { key, value: null, meta: { ...meta, deleted: true } };
}

/**
 * A total order over two versions of the same item.
 *
 * The tie-breaks exist so that BOTH devices independently reach the same
 * answer. If each device merely preferred its own copy on a tie, the two would
 * disagree forever and re-push on every sync.
 */
export function pickNewer<T>(a: SyncItem<T>, b: SyncItem<T>): SyncItem<T> {
  const ta = Date.parse(a.meta.updatedAt);
  const tb = Date.parse(b.meta.updatedAt);
  const aBad = Number.isNaN(ta);
  const bBad = Number.isNaN(tb);

  // An unparseable timestamp loses to a real one rather than poisoning the
  // comparison — `NaN > x` and `NaN < x` are both false, so without this an
  // corrupt row would win or lose depending on argument order.
  if (aBad !== bBad) return aBad ? b : a;

  if (!aBad && !bBad) {
    if (ta > tb) return a;
    if (tb > ta) return b;
  }

  // Same instant: the greater device id wins.
  if (a.meta.updatedBy !== b.meta.updatedBy) {
    return a.meta.updatedBy > b.meta.updatedBy ? a : b;
  }

  // Same instant AND same device, with differing payloads. Not reachable in
  // normal operation, but the final fallback has to be deterministic or
  // `merge(a, b)` and `merge(b, a)` could disagree, which is the one property
  // the whole model rests on.
  const sa = stableString(a);
  const sb = stableString(b);
  return sa >= sb ? a : b;
}

/** Order-independent serialisation, so the last-resort tie-break is stable. */
function stableString(item: SyncItem<unknown>): string {
  return JSON.stringify([item.meta.deleted === true, item.value], (_key, value: unknown) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const record = value as Record<string, unknown>;
      return Object.keys(record).sort().reduce<Record<string, unknown>>((out, k) => {
        out[k] = record[k];
        return out;
      }, {});
    }
    return value;
  });
}

/**
 * Reconcile two item sets.
 *
 * An item present on only one side is taken as-is: that is a create the other
 * side has not seen yet, not a delete. Only a tombstone deletes.
 */
export function mergeItems<T>(local: SyncItem<T>[], remote: SyncItem<T>[]): SyncItem<T>[] {
  const out = new Map<string, SyncItem<T>>();
  for (const item of local) out.set(item.key, item);
  for (const item of remote) {
    const mine = out.get(item.key);
    out.set(item.key, mine ? pickNewer(mine, item) : item);
  }
  // Sorted so the result is a value, not an accident of insertion order —
  // which is what lets a test assert convergence with a plain deep-equal.
  return [...out.values()].sort((a, b) => a.key.localeCompare(b.key));
}

export const MAX_SKEW_MS = 5 * 60 * 1000;

/**
 * Clock skew is the known weakness of last-write-wins, and it cannot be
 * designed away: an edit made offline has no server to ask for the time. Both
 * devices are phones on network time, so skew should be well under a second —
 * but a single badly-set clock would otherwise win EVERY future conflict, for
 * as long as it stayed wrong.
 *
 * So an incoming timestamp more than five minutes ahead of the server is
 * clamped to server time on ingest. Five minutes is wide enough that ordinary
 * drift is untouched and narrow enough that a wrong clock stops being
 * permanent.
 */
export function clampFuture<T>(
  items: SyncItem<T>[],
  serverNowMs: number,
  maxSkewMs: number = MAX_SKEW_MS,
): SyncItem<T>[] {
  const limit = serverNowMs + maxSkewMs;
  return items.map((item) => {
    const at = Date.parse(item.meta.updatedAt);
    if (Number.isNaN(at) || at <= limit) return item;
    return { ...item, meta: { ...item.meta, updatedAt: new Date(serverNowMs).toISOString() } };
  });
}

/** Items this device has changed since it last successfully synced. */
export function pendingSince<T>(items: SyncItem<T>[], lastSyncedAt: string | null): SyncItem<T>[] {
  if (!lastSyncedAt) return items;
  const since = Date.parse(lastSyncedAt);
  if (Number.isNaN(since)) return items;
  // An unparseable item timestamp is treated as pending. Pushing something
  // twice is harmless; never pushing it is data loss.
  return items.filter((item) => {
    const at = Date.parse(item.meta.updatedAt);
    return Number.isNaN(at) || at > since;
  });
}
